import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * Panneau de progression pour les reprises en streaming NDJSON.
 *
 * props.progress : {
 *   current, total, currentLabel, percent?,
 *   metrics: [{ label, value, tone }]
 * }
 * `tone` ∈ 'blue' | 'indigo' | 'emerald' | 'red' | 'gray'
 */
const TONE_CLASS = {
  blue:     'text-blue-600',
  indigo:   'text-indigo-600',
  emerald:  'text-emerald-600',
  red:      'text-red-600',
  gray:     'text-gray-600',
  amber:    'text-amber-600'
};

const BAR_CLASS = {
  emerald: { bar: 'bg-emerald-600', border: 'border-emerald-200', text: 'text-emerald-800' },
  indigo:  { bar: 'bg-indigo-600',  border: 'border-indigo-200',  text: 'text-indigo-800'  },
  rose:    { bar: 'bg-rose-600',    border: 'border-rose-200',    text: 'text-rose-800'    },
  amber:   { bar: 'bg-amber-600',   border: 'border-amber-200',   text: 'text-amber-800'   }
};

const StreamProgressPanel = ({ title = 'Traitement en cours', progress, barColor = 'emerald' }) => {
  if (!progress) return null;
  const total   = progress.total || 0;
  const current = progress.current || 0;
  const pct = typeof progress.percent === 'number'
    ? progress.percent
    : (total > 0 ? Math.min(100, (current / total) * 100) : 0);
  const theme = BAR_CLASS[barColor] || BAR_CLASS.emerald;

  return (
    <div className={`bg-white border-2 ${theme.border} rounded-lg p-5 space-y-3`}>
      <div className={`flex items-center gap-2 ${theme.text} font-semibold`}>
        <ArrowPathIcon className="h-5 w-5 animate-spin" />
        {title} — ne pas fermer cette fenêtre
      </div>
      <div>
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>
            {current} / {total || '?'}
            {progress.currentLabel ? ` — ${progress.currentLabel}` : ''}
          </span>
          <span>{Math.round(pct)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`${theme.bar} h-2 transition-all duration-200`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      {Array.isArray(progress.metrics) && progress.metrics.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {progress.metrics.map((m, i) => (
            <div key={i} className="bg-gray-50 rounded p-2">
              <div className="text-gray-500">{m.label}</div>
              <div className={`text-base font-bold ${TONE_CLASS[m.tone] || 'text-gray-900'}`}>
                {m.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StreamProgressPanel;
